import { useState } from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Marquee from "@/components/Marquee";
import PinnedCard from "@/components/PinnedCard";
import CalendarBoard from "@/components/CalendarBoard";
import EventModal from "@/components/EventModal";
import { NewsletterSection } from "@/components/NewsletterForm";
import { PageIntro, FadeUp } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { useEvents } from "@/hooks/useEvents";

const ROTATIONS = ["-1.5deg", "1deg", "-0.75deg", "1.5deg", "-1deg", "0.75deg"];

const Happening = () => {
    const { t } = useContent();
    const { events } = useEvents();
    const [selected, setSelected] = useState(null);
    return (
        <>
            <Seo
                title="What's Happening"
                description="Real events, workshops, potlucks, and goings-on at Panther City Clubhouse in Richland Hills, TX — synced live from the clubhouse calendar."
            />
            <PageIntro kicker="the bulletin board" title="WHAT'S HAPPENING" intro={t("happening.intro", "")} />
            <section data-testid="happening-board" className="border-y-2 border-ink bg-forest px-5 py-16 sm:px-10 sm:py-20">
                <div className="mx-auto grid max-w-6xl gap-10 sm:grid-cols-2 lg:grid-cols-3">
                    {(events || []).slice(0, 6).map((e, i) => (
                        <FadeUp key={e.id} delay={(i % 3) * 0.08}>
                            <PinnedCard event={e} rotate={ROTATIONS[i % ROTATIONS.length]} onSelect={setSelected} />
                        </FadeUp>
                    ))}
                    {events && events.length === 0 && (
                        <p data-testid="happening-empty" className="font-hand text-3xl text-cream/85">
                            Nothing pinned up just yet. Check back soon.
                        </p>
                    )}
                    {!events && <p className="font-hand text-2xl text-cream/70">Pinning things up…</p>}
                </div>
                <FadeUp className="mx-auto mt-14 max-w-6xl">
                    <div className="flex flex-col items-start gap-4 border-2 border-dashed border-cream/50 p-6 sm:flex-row sm:items-center sm:justify-between">
                        <p className="text-cream/85">
                            Want to put something on this board? If you can gather five people around a table, we'll help you do it.
                        </p>
                        <Link to="/get-involved" data-testid="happening-cta-host" className="btn-stamp !bg-mustard !text-ink">
                            Host Something
                        </Link>
                    </div>
                </FadeUp>
            </section>
            <section data-testid="happening-calendar" className="px-5 py-16 sm:px-10 sm:py-20">
                <div className="mx-auto max-w-6xl">
                    <FadeUp>
                        <p className="font-hand text-2xl text-orange -rotate-2 sm:text-3xl">straight off the clubhouse calendar</p>
                        <h2 className="mt-1 font-display text-4xl tracking-wide text-ink sm:text-5xl">THE LIVING CALENDAR</h2>
                        <p className="mt-3 max-w-2xl text-ink/75">
                            Every public event at the Clubhouse, synced straight from the calendar we actually keep. Flip between the week
                            and the month, tap anything for the details. It updates itself — no thumbtacks required.
                        </p>
                    </FadeUp>
                    <FadeUp delay={0.1} className="mt-8">
                        <CalendarBoard events={events || []} onSelect={setSelected} />
                    </FadeUp>
                </div>
            </section>
            <section data-testid="happening-studio" className="border-y-2 border-ink bg-orange px-5 py-14 sm:px-10">
                <div className="mx-auto flex max-w-6xl flex-col items-start gap-6 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="font-hand text-2xl text-cream -rotate-2 sm:text-3xl">{t("studio.kicker", "NEED THE WORK ROOM?")}</p>
                        <p className="mt-2 max-w-xl text-cream/90">
                            {t("studio.body", "Reserve the Work Room all to yourself for meetings, calls, recording, making, or whatever brilliant little thing you're cooking up. Members get a deal; neighbors pay the public rate.")}
                        </p>
                    </div>
                    <Link to="/work-room" data-testid="workroom-book-cta" className="btn-stamp !bg-cream !text-ink !text-xl">
                        {t("studio.cta", "Reserve the Work Room")} →
                    </Link>
                </div>
            </section>
            <NewsletterSection idPrefix="happening-newsletter" />
            <Marquee invert />
            <EventModal event={selected} onClose={() => setSelected(null)} />
        </>
    );
};

export default Happening;
